import {
  useDraftEngineeringJudgementMutation,
  useSubmitEngineeringJudgementMutation,
} from 'schema'
import { useEJLogContext } from '../../../../EJLogContext'
import { useProjectCustomFieldsContext } from 'components/CustomFields/ProjectCustomFieldsContext'
import { useForm, useWatch } from 'antd/lib/form/Form'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { simpleSuccessNotification } from 'hooks/notificationHandler'
import { useEffect, useState } from 'react'
import FormlessModal from 'components/CIMSModal/FormlessModal'
import { Button, Checkbox, Form, Space } from 'antd'
import { usePayItemSelectContext } from 'components/CIMSSelect/common/PayItemSelect/PayItemSelectContext'
import Tooltip from 'antd/es/tooltip'
import { useEJReportsSelectOptionsContext } from '../../../../../EJReportsSelectOptionsContext'
import { DraftSubmitEJModalValues } from './types'
import { toMoment } from 'utils/toMoment'
import CommonEJFormItems from '../CommonEJFormItems'
import CreateEditEJFormItems from '../CreateEditEJFormItems'
import { useAuthContext } from 'context/AuthContext'
import { Typography } from 'antd'
import { useProjectContext } from 'context/ProjectContext'

const DraftSubmitEJModal: React.FC = () => {
  const { user } = useAuthContext()
  const { projectId } = useProjectContext()
  const {
    metadata: { loading: metaFetching, ejTypes, ejQuickLinks },
    typeOptions,
    refetch,
    edit,
    draftSubmitModal: { state, toggle, visible },
  } = useEJLogContext()
  const { toMutateCustomFieldsInput, toCustomFieldEditorValue } =
    useProjectCustomFieldsContext()
  const {
    toLabeledValues: toLabeledPayItems,
    fromLabeledValues: fromLabeledPayItems,
  } = usePayItemSelectContext()
  const {
    toLabeledValues: toLabeledReports,
    fromLabeledValues: fromLabeledReports,
  } = useEJReportsSelectOptionsContext()
  const [form] = useForm<DraftSubmitEJModalValues>()
  const [canSubmit, setCanSubmit] = useState<boolean>(false)
  const [submitTooltip, setSubmitTooltip] = useState<string>('')
  const [mutating, setMutating] = useState<boolean>(false)
  const typeIdWatch = useWatch('typeId', form)
  const failureDateWatch = useWatch('failureDate', form)
  const failureDescriptionWatch = useWatch('failureDescription', form)
  const justificationWatch = useWatch('justification', form)
  const notesWatch = useWatch('notes', form)
  const reportsWatch = useWatch('reports', form)
  const manualReportIdWatch = useWatch('manualReportId', form)
  const [draftEJ] = useDraftEngineeringJudgementMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      draftEngineeringJudgement: {
        judgement: { formattedNumber },
      },
    }) => simpleSuccessNotification(`${formattedNumber} drafted successfully!`),
  })
  const [submitEJ] = useSubmitEngineeringJudgementMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      submitEngineeringJudgement: {
        judgement: { formattedNumber },
      },
    }) =>
      simpleSuccessNotification(`${formattedNumber} submitted successfully!`),
  })
  useEffect(() => {
    const missingFields = []
    if (!manualReportIdWatch && !((reportsWatch ?? []).length > 0))
      missingFields.push('Reports')
    if (!failureDescriptionWatch) missingFields.push('Failure Description')
    if (!justificationWatch) missingFields.push('Justification')
    if (!notesWatch) missingFields.push('Notes')
    const allFieldsFilled =
      !!typeIdWatch &&
      !!failureDateWatch &&
      !!failureDescriptionWatch &&
      !!justificationWatch &&
      !!notesWatch &&
      (!!manualReportIdWatch || (reportsWatch ?? []).length > 0)

    if (!allFieldsFilled) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Stop-gap for existing behavior.
      setCanSubmit(false)
      setSubmitTooltip(
        `Missing fields required to Submit: ${missingFields.join(', ')} `
      )
    } else if (
      !user?.implicitlyAuthorized({
        users: ejTypes?.find(({ id }) => id === typeIdWatch)?.users,
        projectId,
      }).authorized
    ) {
      setCanSubmit(false)
      setSubmitTooltip('Must be a member of selected Type to Submit')
    } else {
      setCanSubmit(true)
      setSubmitTooltip('')
    }
  }, [
    typeIdWatch,
    failureDateWatch,
    failureDescriptionWatch,
    justificationWatch,
    notesWatch,
    manualReportIdWatch,
    reportsWatch,
    ejTypes,
    user,
    projectId,
  ])
  useEffect(() => {
    form.setFieldsValue({
      submit: false,
      typeId: state?.typeId,
      failureDate: toMoment(state?.failureDate),
      failureDescription: state?.failureDescription,
      justification: state?.justification,
      notes: state?.notes,
      manualReportId: state?.manualReportId,
      reports: toLabeledReports(
        state?.ejToReports?.map(
          ({
            concreteTest,
            concretePlacement,
            lot,
            dailyInspectionControlPoint,
            dailyInspectionInspection,
          }) =>
            concreteTest ??
            concretePlacement ??
            lot ??
            dailyInspectionControlPoint ??
            dailyInspectionInspection!
        )
      ),
      payItems: toLabeledPayItems(
        state?.ejToPayItemNoGeometries?.map(({ payItem }) => payItem)
      ),
      externalLinks: state?.externalLinks,
      customFields: toCustomFieldEditorValue(state?.customFields),
    })
  }, [
    form,
    state,
    toCustomFieldEditorValue,
    toLabeledPayItems,
    toLabeledReports,
  ])

  const resetFields = () =>
    form.setFieldsValue({
      submit: false,
      typeId: undefined,
      failureDate: undefined,
      failureDescription: null,
      justification: null,
      notes: null,
      reports: [],
      manualReportId: null,
      payItems: [],
      externalLinks: [],
      customFields: undefined,
    })

  return (
    <FormlessModal
      title={`${!!state ? 'Draft' : 'Create'} Engineering Judgement`}
      visible={visible}
      ok={false}
      cancel={{ fn: () => toggle(null) }}
      afterClose={() => resetFields()}
      footer={
        <Space>
          <Button onClick={() => toggle(null)}>Cancel</Button>
          <Button
            onClick={() => {
              form.setFieldValue('submit', false)
              form.submit()
            }}
            loading={mutating}
          >
            Save as Draft
          </Button>
          <Tooltip title={submitTooltip}>
            <Button
              type="primary"
              onClick={() => {
                form.setFieldValue('submit', true)
                form.submit()
              }}
              disabled={!canSubmit}
              loading={mutating}
            >
              Submit
            </Button>
          </Tooltip>
        </Space>
      }
    >
      <Space size="small" direction="vertical">
        <Typography.Paragraph type="secondary">
          In order for an issuer to Submit an Engineering Judgement, the
          following fields must be filled out: Reports, Failure Description,
          Justification, and Notes
        </Typography.Paragraph>
        <Form
          form={form}
          initialValues={{
            submit: false,
            typeId: state?.typeId,
            failureDate: toMoment(state?.failureDate),
            failureDescription: state?.failureDescription,
            justification: state?.justification,
            notes: state?.notes,
            manualReportId: state?.manualReportId,
            reports: toLabeledReports(
              state?.ejToReports?.map(
                ({
                  concreteTest,
                  concretePlacement,
                  lot,
                  dailyInspectionControlPoint,
                  dailyInspectionInspection,
                }) =>
                  concreteTest ??
                  concretePlacement ??
                  lot ??
                  dailyInspectionControlPoint ??
                  dailyInspectionInspection!
              )
            ),
            payItems: toLabeledPayItems(
              state?.ejToPayItemNoGeometries?.map(({ payItem }) => payItem)
            ),
            externalLinks: state?.externalLinks,
            customFields: toCustomFieldEditorValue(state?.customFields),
          }}
          onFinish={async ({
            submit,
            failureDate,
            reports,
            payItems,
            customFields,
            externalLinks,
            failureDescription,
            justification,
            notes,
            ...rest
          }: DraftSubmitEJModalValues) => {
            setMutating(true)
            if (submit) {
              await submitEJ({
                variables: {
                  input: {
                    id: state?.id,
                    projectId,
                    // Failure date form field is required, so using bang
                    failureDate: failureDate!.toISOString(),
                    reports: fromLabeledReports(reports),
                    payItemIds: fromLabeledPayItems(payItems),
                    externalLinks: externalLinks?.map(({ href, label }) => ({
                      href,
                      label,
                    })),
                    customFields: toMutateCustomFieldsInput(customFields),
                    failureDescription: failureDescription!,
                    justification: justification!,
                    notes: notes!,
                    ...(rest as Omit<
                      Required<DraftSubmitEJModalValues>,
                      | 'payItemIds'
                      | 'reports'
                      | 'failureDate'
                      | 'externalLinks'
                      | 'customFields'
                      | 'failureDescription'
                      | 'justification'
                      | 'notes'
                    >),
                  },
                },
              })
            } else if (!submit && state) {
              // saving an existing draft as draft
              await edit({
                variables: {
                  input: {
                    id: state.id,
                    // Failure date form field is required, so using bang
                    failureDate: failureDate!.toISOString(),
                    reports: fromLabeledReports(reports),
                    payItemIds: fromLabeledPayItems(payItems),
                    customFields: toMutateCustomFieldsInput(customFields),
                    failureDescription,
                    justification,
                    notes,
                    externalLinks: externalLinks?.map(({ href, label }) => ({
                      href,
                      label,
                    })),
                    ...rest,
                  },
                },
              })
            } else {
              await draftEJ({
                variables: {
                  input: {
                    projectId,
                    // Failure date form field is required, so using bang
                    failureDate: failureDate!.toISOString(),
                    reports: fromLabeledReports(reports),
                    payItemIds: fromLabeledPayItems(payItems),
                    externalLinks,
                    customFields: toMutateCustomFieldsInput(customFields),
                    failureDescription,
                    justification,
                    notes,
                    ...rest,
                  },
                },
              })
            }
            setMutating(false)
            refetch()
            toggle(null)
          }}
        >
          <Form.Item hidden name="submit" valuePropName="checked">
            <Checkbox />
          </Form.Item>
          <CommonEJFormItems
            form={form}
            types={ejTypes}
            typeOptions={typeOptions}
            {...state}
          />
          <CreateEditEJFormItems
            loading={metaFetching}
            quickLinks={ejQuickLinks}
            {...state}
          />
        </Form>
      </Space>
    </FormlessModal>
  )
}

export default DraftSubmitEJModal
